import { z } from 'zod';
import {
  McpCapabilityMismatchSchema,
  McpProbeSummaryEntrySchema,
  McpServerCapabilitiesSchema,
  McpServerInfoSchema,
} from '@terra/shared-contracts-mcp-servers';

export const McpEntityKindSchema = z.enum([
  'SERVER',
  'TOOL',
  'PROMPT',
  'RESOURCE',
]);

export type McpEntityKindDto = z.infer<typeof McpEntityKindSchema>;

export const McpEntityRefSchema = z.object({
  serverId: z.string(),
  kind: McpEntityKindSchema.optional(),
  toolName: z.string().optional(),
  promptName: z.string().optional(),
  resourceUri: z.string().optional(),
  resourceUriTemplate: z.string().optional(),
  paramPath: z.string().optional(),
  definitionHash: z.string().optional(),
  injectable: z.boolean().optional(),
});

export type McpEntityRefDto = z.infer<typeof McpEntityRefSchema>;

export const McpToolCallOutcomeSchema = z.enum([
  'success',
  'denied',
  'insufficient_scope',
  'error',
  'untested',
]);

export type McpToolCallOutcomeDto = z.infer<typeof McpToolCallOutcomeSchema>;

export const McpIdentityAccessEntrySchema = z.object({
  identityId: z.string(),
  label: z.string(),
  visible: z.boolean().optional(),
  accessible: z.boolean().optional(),
  missingScopes: z.array(z.string()).optional(),
  callOutcome: McpToolCallOutcomeSchema.optional(),
  requiredScopesFromChallenge: z.array(z.string()).optional(),
  callObservedAt: z.string().optional(),
});

export type McpIdentityAccessEntryDto = z.infer<
  typeof McpIdentityAccessEntrySchema
>;

export const McpAssetAccessScopeSchema = z.enum(['ALL', 'SOME']);

export type McpAssetAccessScopeDto = z.infer<typeof McpAssetAccessScopeSchema>;

export const McpIdentityAuthStatusSchema = z.enum([
  'authorized',
  'auth_required',
  'insufficient_scope',
  'unknown',
]);

export type McpIdentityAuthStatusDto = z.infer<
  typeof McpIdentityAuthStatusSchema
>;

export const McpIdentityRosterEntrySchema = z.object({
  identityId: z.string(),
  label: z.string(),
  connectionSuccess: z.boolean(),
  error: z.string().nullable().optional(),
  grantedScopes: z.array(z.string()).optional(),
  missingServerScopes: z.array(z.string()).optional(),
  challengeScopes: z.array(z.string()).optional(),
  authStatus: McpIdentityAuthStatusSchema.optional(),
});

export type McpIdentityRosterEntryDto = z.infer<
  typeof McpIdentityRosterEntrySchema
>;

export const McpServerAuthMetadataSchema = z.object({
  scopesSupported: z.array(z.string()),
  authorizationServers: z.array(z.string()),
  requiresAuth: z.boolean(),
  anonAccessible: z.boolean().optional(),
});

export type McpServerAuthMetadataDto = z.infer<
  typeof McpServerAuthMetadataSchema
>;

export const McpDefinitionHashHistoryEntrySchema = z.object({
  hash: z.string(),
  at: z.string(),
});

export type McpDefinitionHashHistoryEntryDto = z.infer<
  typeof McpDefinitionHashHistoryEntrySchema
>;

export const McpServerComponentMetadataSchema = z.object({
  transport: z.string().optional(),
  url: z.string().optional(),
  protocolVersion: z.string().nullable().optional(),
  serverInfo: McpServerInfoSchema.nullable().optional(),
  capabilities: McpServerCapabilitiesSchema.nullable().optional(),
  authModel: z.string().nullable().optional(),
  authScopes: z.array(z.string()).optional(),
  auth: McpServerAuthMetadataSchema.optional(),
  supportedMethods: z.array(z.string()).optional(),
  probeSummary: z.array(McpProbeSummaryEntrySchema).optional(),
  capabilityMismatches: z.array(McpCapabilityMismatchSchema).optional(),
  toolCount: z.number().optional(),
  promptCount: z.number().optional(),
  resourceCount: z.number().optional(),
  latencyMs: z.number().optional(),
  connectionSuccess: z.boolean().optional(),
  discoveryOwned: z.boolean().optional(),
  mcpEntity: McpEntityRefSchema.optional(),
  identityRoster: z.array(McpIdentityRosterEntrySchema).optional(),
});

export type McpServerComponentMetadataDto = z.infer<
  typeof McpServerComponentMetadataSchema
>;

export const McpToolDeclaredMetadataSchema = z.object({
  description: z.string().nullable().optional(),
  inputSchema: z.record(z.string(), z.unknown()).nullable(),
  outputSchema: z.record(z.string(), z.unknown()).nullable(),
  annotations: z.record(z.string(), z.unknown()).nullable(),
  definitionHash: z.string(),
  requiredScopes: z.array(z.string()).optional(),
});

export type McpToolDeclaredMetadataDto = z.infer<
  typeof McpToolDeclaredMetadataSchema
>;

export const McpToolObservedMetadataSchema = z.object({
  args: z.record(z.string(), z.unknown()).optional(),
  result: z.unknown().optional(),
  error: z.string().nullable().optional(),
  dataAccess: z.array(z.string()).optional(),
});

export type McpToolObservedMetadataDto = z.infer<
  typeof McpToolObservedMetadataSchema
>;

export const McpToolComponentMetadataSchema = z.object({
  mcpEntity: McpEntityRefSchema,
  declared: McpToolDeclaredMetadataSchema,
  observed: McpToolObservedMetadataSchema.optional(),
  discoveryOwned: z.boolean().optional(),
  probeStatus: z.string().optional(),
  source: z.string().optional(),
  identityAccess: z.array(McpIdentityAccessEntrySchema).optional(),
  accessScope: McpAssetAccessScopeSchema.optional(),
  driftDetected: z.boolean().optional(),
  driftFrom: z.string().nullable().optional(),
  definitionHashHistory: z
    .array(McpDefinitionHashHistoryEntrySchema)
    .optional(),
});

export type McpToolComponentMetadataDto = z.infer<
  typeof McpToolComponentMetadataSchema
>;

export const McpPromptComponentMetadataSchema = z.object({
  mcpEntity: McpEntityRefSchema,
  arguments: z.array(
    z.object({
      name: z.string(),
      description: z.string().nullable().optional(),
      required: z.boolean().optional(),
    }),
  ),
  templateBody: z.string().nullable().optional(),
  discoveryOwned: z.boolean().optional(),
  identityAccess: z.array(McpIdentityAccessEntrySchema).optional(),
  accessScope: McpAssetAccessScopeSchema.optional(),
  driftDetected: z.boolean().optional(),
  driftFrom: z.string().nullable().optional(),
  definitionHashHistory: z
    .array(McpDefinitionHashHistoryEntrySchema)
    .optional(),
});

export type McpPromptComponentMetadataDto = z.infer<
  typeof McpPromptComponentMetadataSchema
>;

export const McpResourceComponentMetadataSchema = z
  .object({
    mcpEntity: McpEntityRefSchema,
    uri: z.string().optional(),
    uriTemplate: z.string().optional(),
    mimeType: z.string().nullable().optional(),
    uriTemplates: z
      .array(
        z.object({
          uriTemplate: z.string(),
          name: z.string().nullable(),
          mimeType: z.string().nullable(),
        }),
      )
      .optional(),
    sampleContent: z.string().nullable().optional(),
    discoveryOwned: z.boolean().optional(),
    identityAccess: z.array(McpIdentityAccessEntrySchema).optional(),
    accessScope: McpAssetAccessScopeSchema.optional(),
    driftDetected: z.boolean().optional(),
    driftFrom: z.string().nullable().optional(),
    definitionHashHistory: z
      .array(McpDefinitionHashHistoryEntrySchema)
      .optional(),
  })
  .refine(
    (metadata) =>
      metadata.uri !== undefined ||
      metadata.uriTemplate !== undefined ||
      metadata.mcpEntity.resourceUri !== undefined ||
      metadata.mcpEntity.resourceUriTemplate !== undefined,
    { message: 'MCP resource metadata requires uri or uriTemplate' },
  );

export type McpResourceComponentMetadataDto = z.infer<
  typeof McpResourceComponentMetadataSchema
>;

export const McpProtocolComponentMetadataSchema = z.object({
  mcpEntity: McpEntityRefSchema.extend({
    jsonRpcMethod: z.string().optional(),
  }),
  jsonRpcMethod: z.string(),
  probeStatus: z.string(),
  declaredInCapabilities: z.boolean(),
  capabilityFamily: z.enum(['tools', 'prompts', 'resources']).nullable(),
  discoveryOwned: z.boolean().optional(),
  identityAccess: z.array(McpIdentityAccessEntrySchema).optional(),
  accessScope: McpAssetAccessScopeSchema.optional(),
});

export type McpProtocolComponentMetadataDto = z.infer<
  typeof McpProtocolComponentMetadataSchema
>;
